import { useState, useCallback, useEffect, useRef } from 'react';
import { ResponsiveGridLayout, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import type { Layout, ResponsiveLayouts } from 'react-grid-layout';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { useEditModeStore } from '../useEditModeStore';
import { useWidgetStore } from './useWidgetStore';
import { WIDGET_MAP, type WidgetDef } from './widgetRegistry';
import { WidgetWrapper } from './WidgetWrapper';
import { WidgetAddDrawer } from './WidgetAddDrawer';
import { widgetStaggerContainer } from './widgetAnimations';

import 'react-grid-layout/css/styles.css';

function useMeasure(ref: React.RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setSize({ w: Math.round(entry.contentRect.width), h: Math.round(entry.contentRect.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

function GridItem({ widget, isEditMode, isMobile, removeWidget, handleExpand, availableWidgets, setDrawerOpen }: {
  widget: WidgetDef;
  isEditMode: boolean;
  isMobile: boolean;
  removeWidget: (id: string) => void;
  handleExpand: (id: string) => void;
  availableWidgets: WidgetDef[];
  setDrawerOpen: (open: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const size = useMeasure(ref);
  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      <WidgetWrapper
        def={widget}
        isEditMode={isEditMode}
        isMobile={isMobile}
        width={size.w}
        height={size.h}
        onRemove={removeWidget}
        onExpand={handleExpand}
        onOpenAddDrawer={availableWidgets.length > 0 ? () => setDrawerOpen(true) : undefined}
      />
    </div>
  );
}

export function WidgetGrid() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const { isEditMode } = useEditModeStore();
  const {
    activeWidgets,
    availableWidgets,
    layouts,
    onLayoutChange,
    addWidget,
    removeWidget,
    resetToDefaults,
  } = useWidgetStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedWidget, setExpandedWidget] = useState<string | null>(null);

  const { width, containerRef, mounted } = useContainerWidth();

  const handleLayoutChange = useCallback(
    (layout: Layout, allLayouts: ResponsiveLayouts) => {
      onLayoutChange(layout, allLayouts);
    },
    [onLayoutChange],
  );

  const handleExpand = useCallback((id: string) => {
    setExpandedWidget(id);
  }, []);

  const handleCollapseExpand = useCallback(() => {
    setExpandedWidget(null);
  }, []);

  // Close expanded widget on Escape
  useEffect(() => {
    if (!expandedWidget) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedWidget(null);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expandedWidget]);

  if (activeWidgets.length === 0 && availableWidgets.length === 0) return null;

  return (
    <>
      <motion.div
        ref={containerRef as React.RefObject<HTMLDivElement>}
        className="px-4 md:px-10"
        data-edit-zone
        variants={widgetStaggerContainer}
        initial="hidden"
        animate="visible"
      >
        {mounted && (
          <ResponsiveGridLayout
            width={width}
            breakpoints={{ lg: 1200, md: 768, sm: 0 }}
            cols={{ lg: 12, md: 12, sm: 1 }}
            rowHeight={150}
            layouts={layouts}
            onLayoutChange={handleLayoutChange}
            dragConfig={{
              enabled: !isMobile,
              handle: '.widget-drag-handle',
            }}
            resizeConfig={{
              enabled: !isMobile,
            }}
            compactor={verticalCompactor}
            margin={[24, 24]}
            containerPadding={[0, 0]}
          >
            {activeWidgets.map((widget) => (
              <div key={widget.id}>
                <GridItem
                  widget={widget}
                  isEditMode={isEditMode}
                  isMobile={isMobile}
                  removeWidget={removeWidget}
                  handleExpand={handleExpand}
                  availableWidgets={availableWidgets}
                  setDrawerOpen={setDrawerOpen}
                />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}

        {/* Edit mode actions */}
        {isEditMode && (
          <motion.div
            className="mt-4 flex items-center justify-center gap-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.button
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-lg backdrop-blur-sm ${
                availableWidgets.length > 0
                  ? 'bg-primary-500/90 text-white'
                  : 'bg-gray-300/60 text-gray-400 cursor-not-allowed'
              }`}
              whileTap={availableWidgets.length > 0 ? { scale: 0.95 } : undefined}
              onClick={() => availableWidgets.length > 0 && setDrawerOpen(true)}
            >
              <Plus size={16} />
              {t('home.widgets.addWidget')}
            </motion.button>
            <motion.button
              className="flex items-center gap-1.5 rounded-full bg-white/60 px-3 py-2 text-sm font-medium text-gray-600 shadow-sm backdrop-blur-sm"
              whileTap={{ scale: 0.95 }}
              onClick={resetToDefaults}
            >
              <RotateCcw size={14} />
              {t('home.widgets.resetLayout')}
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      {/* Add widget drawer */}
      <WidgetAddDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        widgets={availableWidgets}
        onAdd={addWidget}
      />

      {/* Fullscreen expand overlay */}
      <AnimatePresence>
        {expandedWidget && (() => {
          const def = WIDGET_MAP.get(expandedWidget);
          if (!def) return null;
          const Component = def.component;
          return (
            <motion.div
              key="widget-focus-overlay"
              className="widget-focus-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCollapseExpand}
            >
              <motion.div
                className="glass-surface-widget m-4 h-[calc(100%-2rem)] w-[calc(100%-2rem)] overflow-auto rounded-[32px] p-6 md:m-8 md:h-[calc(100%-4rem)] md:w-[calc(100%-4rem)]"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Component width={800} height={600} isEditMode={false} />
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </>
  );
}
